import * as React from 'react';
// mui
import { FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
// components
import DateRangePicker, { MuiDateRange } from 'src/components/DateRangePicker';
// libraries
import moment from 'moment';
// types
import { Timeframe, DateRange } from 'src/types';

export interface TimeframeDateRange {
  timeframe: Timeframe;
  dateRange: DateRange;
}

let customTimeframe = {
  label: 'custom',
  days: 3,
} as Timeframe;

export const timeframes: Timeframe[] = [
  { label: '7 days', days: 7 },
  { label: '4 weeks', days: 28 },
  { label: '6 months', days: 180 },
  { label: '12 months', days: 365 },
  customTimeframe,
];

// Takes the endDate into account (makes it behavior similar to how Stripe's date range picker works)

export const timeframeToDateRange = (timeframe: Timeframe): DateRange => {
  const startDate = moment().subtract(timeframe.days, 'days').toDate();
  const endDate = new Date();

  return {
    startDate,
    endDate,
  };
};

export const dateRangeToTimeframe = (dateRange: DateRange): Timeframe => {
  const days = moment(dateRange.endDate).diff(dateRange.startDate, 'days');

  return timeframes.find((x) => x.days === days) ?? customTimeframe;
};

interface ReportTimeFrameInputProps {
  defaultTimeframe: Timeframe;
  onChange: (timeframeDateRange: TimeframeDateRange) => void;
}

const ReportTimeframeInput: React.FC<ReportTimeFrameInputProps> = ({
  defaultTimeframe,
  onChange,
}) => {
  // Timeframe and date range both depend on each other and are modified when the 'Select' or 'DateRangePicker' value changes.
  const [timeframe, setTimeframe] = React.useState<Timeframe>(defaultTimeframe);
  const [dateRange, setDateRange] = React.useState<DateRange>(timeframeToDateRange(timeframe));

  React.useEffect(() => {
    onChange({
      timeframe: timeframe,
      dateRange: timeframeToDateRange(timeframe),
    });
  }, []); // eslint-disable-line

  const handleTimeframeChange = (event: SelectChangeEvent<Timeframe>) => {
    const timeframe = event.target.value as Timeframe;
    setTimeframe(timeframe);
    onChange({
      timeframe: timeframe,
      dateRange: timeframeToDateRange(timeframe),
    });
    setDateRange(timeframeToDateRange(timeframe));
  };

  const handleDatePickerChange = (range: MuiDateRange) => {
    if (range[0] && range[1]) {
      const timeframeDateRange: TimeframeDateRange = {
        timeframe: customTimeframe,
        dateRange: {
          startDate: range[0],
          endDate: range[1],
        },
      };
      setDateRange(timeframeDateRange.dateRange);
      onChange(timeframeDateRange);
      setTimeframe(customTimeframe);
    }
  };

  const getLabel = (timeframeLabel: string) => {
    if (timeframeLabel === 'custom') {
      return 'Custom';
    }

    return `Last ${timeframeLabel}`;
  };

  return (
    <>
      <FormControl fullWidth={true}>
        <Grid container sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid item xs={12} sm={5} md={4}>
            <InputLabel id="simple-select-disabled-label" sx={{ mt: -1 }}>Date Range</InputLabel>
            <Select
              labelId="simple-select-disabled-label"
              value={timeframe}
              displayEmpty
              fullWidth={true}
              onChange={handleTimeframeChange}
            >
              {timeframes.map((item) => (
                // @ts-ignore
                <MenuItem key={item.label} value={item}>
                  {getLabel(item.label)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid
            item
            xs={12}
            sm={7}
            md={6}
            sx={{ mt: { xs: 1.5, sm: 0 }, pl: { sm: 1.5 } }}
          >
            <DateRangePicker
              onChange={(date) => {}}
              onAccept={handleDatePickerChange}
              value={[dateRange.startDate, dateRange.endDate]}
            />
          </Grid>
        </Grid>
      </FormControl>
    </>
  );
};

export default ReportTimeframeInput;
